import type { TFunction } from 'i18next';

export type ActionLogChange = {
  field: string;
  previous: unknown;
  current: unknown;
};

/**
 * Fields the API records diffs for. Each one owns an `activity.field.<leaf>`
 * translation; the spec asserts every locale carries all of them, so anything
 * added to a Go snapshot struct belongs here too.
 */
export const LOGGED_FIELD_LABELS: readonly string[] = [
  // streamer settings
  'allowDuplicates',
  'allowLiveLearns',
  'alwaysShowRequestDialog',
  'canAnonymousEnterName',
  'canAnonymousRequest',
  'canUserRequest',
  'concurrentRequests',
  'concurrentRequestsPerAnonymous',
  'concurrentRequestsPerUser',
  'donationPage',
  'donationsIgnoreLimits',
  'embedOrigins',
  'enableCrossPlatformRequests',
  'limitAnonymousRequests',
  'liveLearnsNoSongFound',
  'maxRequests',
  'minAmount',
  'minLiveLearnAmount',
  'minTipAmountForQueueOrdering',
  'minTokensForBump',
  'minTokensToIgnoreLimits',
  'queueOrderMode',
  'fixedTokenPrice',
  'minutesBetweenRequests',
  'newDays',
  'promoteQueueToPlaying',
  'queueMethod',
  'requestMode',
  'requestText',
  'requestsActive',
  'requestsPerAnonymous',
  'requestsPerUser',
  'sessionLength',
  'showDonationLink',
  'theme',
  'trackSongDuration',
  // grouped config objects
  'adminSyncConfig',
  'customTheme',
  'eventConfig',
  'featureConfig',
  'homePageConfig',
  'landingPageContent',
  'learnListConfig',
  'permitConfig',
  'queuePageConfig',
  'queuePageConfig.streamer',
  'queuePageConfig.public',
  'songListConfig',
  'songListConfig.titleSortIgnorePrefixes',
  'songListConfig.artistSortIgnorePrefixes',
  'featureConfig.savedRequests',
  'featureConfig.premiumContent',
  'featureConfig.songsToLearn',
  'featureConfig.tokens',
  'featureConfig.collabSessions',
  'featureConfig.duration',
  'featureConfig.globalSongSearch',
  // songs
  'title',
  'artist',
  'capo',
  'comment',
  'active',
  'lyrics',
  'chords',
  'tabs',
  'bypassRequestLimits',
  'attributeIds',
  // queue and history
  'songId',
  'nonlistSong',
  'nonlistTitle',
  'nonlistArtist',
  'note',
  'orderedPosition',
  'priority',
  'playedAt',
  // commands
  'name',
  'action',
  'aliases',
  'cooldown',
  'response',
  'altResponses',
  'options',
  'enabled',
  'adminOnly',
  // integrations
  'botConnected',
  'sendChatMessage',
  'concurrent',
  'limit',
  'requests',
];

function readRecord(value: unknown, key: string): Record<string, unknown> | undefined {
  if (value == null || typeof value !== 'object') return undefined;
  const nested = (value as Record<string, unknown>)[key];
  if (nested == null || typeof nested !== 'object' || Array.isArray(nested)) return undefined;
  return nested as Record<string, unknown>;
}

/**
 * Fields that were logged before they had a user-meaningful form. `position`
 * held the sparse internal ordering column (1024, 2048, …), which reads as a
 * meaningless number; it was replaced by `orderedPosition`.
 */
const OBSOLETE_FIELDS = new Set(['position']);

/**
 * Update payloads carry `fields` (what each value was) and, for logs written
 * once both sides were recorded, `newFields` (what it became). Older rows have
 * only the previous side, so `current` stays undefined rather than misreporting.
 */
export function extractChanges(data: unknown): ActionLogChange[] {
  const previous = readRecord(data, 'fields');
  if (!previous) return [];

  const current = readRecord(data, 'newFields');

  return Object.keys(previous)
    .filter((field) => !OBSOLETE_FIELDS.has(field))
    .sort((a, b) => a.localeCompare(b))
    .map((field) => ({
      field,
      previous: previous[field],
      current: current?.[field],
    }));
}

function humanise(segment: string): string {
  const words = segment
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function leafLabel(t: TFunction, segment: string): string {
  return t(`activity.field.${segment}`, { defaultValue: humanise(segment) });
}

/**
 * Nested config paths keep their group so `featureConfig.savedRequests` reads
 * as "Features › Saved requests" instead of a bare, ambiguous leaf.
 */
export function fieldLabel(t: TFunction, field: string): string {
  const segments = field.split('.');
  const leaf = leafLabel(t, segments[segments.length - 1]);
  if (segments.length === 1) return leaf;

  const groups = segments
    .slice(0, -1)
    .map((segment) => t(`activity.field.group.${segment}`, { defaultValue: humanise(segment) }));

  return [...groups, leaf].join(' › ');
}

/**
 * One compact line per change, for surfaces too small for the diff table —
 * the queue page's activity popover shows these beside the timestamp so a
 * position move is readable without opening the full activity page.
 */
export function summariseChanges(t: TFunction, changes: ActionLogChange[]): string[] {
  return changes.map((change) => {
    const field = fieldLabel(t, change.field);
    const from = formatFieldValue(t, change.previous);

    return change.current === undefined
      ? t('activity.changes.summary_previous', { field, from })
      : t('activity.changes.summary', { field, from, to: formatFieldValue(t, change.current) });
  });
}

export function formatFieldValue(t: TFunction, value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? t('activity.value.on') : t('activity.value.off');
  }

  if (value == null || value === '') return t('activity.value.empty');

  if (Array.isArray(value)) {
    return value.length === 0
      ? t('activity.value.empty')
      : t('activity.value.list', { count: value.length });
  }

  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return value.toString();

  return t('activity.value.complex');
}
